import { isTraceableOrigin } from './allow-list';
import { getActiveTraceparent } from './context';
import { isTracingEnabled } from './enabled';
import { isValidTraceparent } from './traceparent';

const TRACEPARENT_HEADER = 'traceparent';

/**
 * A whatwg `Headers` instance (RTK Query `prepareHeaders`) or a plain header
 * record (WebView bridge / raw `fetch` call sites) — both are supported.
 */
export type MutableHeaders = Headers | Record<string, unknown>;

export interface ApplyTraceparentOptions {
  /**
   * Resolved cluster API base URL. Its origin is allow-listed even when it is
   * not a `*.myosh.com` host (some clusters use a custom domain).
   */
  allowedBaseUrl?: string;
}

const isHeadersInstance = (headers: MutableHeaders): headers is Headers =>
  typeof (headers as Headers).set === 'function';

const setHeader = (headers: MutableHeaders, name: string, value: string): void => {
  if (isHeadersInstance(headers)) {
    headers.set(name, value);
  } else {
    headers[name] = value;
  }
};

const hasHeader = (headers: MutableHeaders, name: string): boolean => {
  if (isHeadersInstance(headers)) {
    return headers.has(name); // Headers lookups are case-insensitive
  }
  const lowerName = name.toLowerCase();
  return Object.keys(headers).some((key) => key.toLowerCase() === lowerName);
};

/**
 * Inject a W3C `traceparent` into `headers` when `targetUrl` is a myosh origin.
 *
 * No-op (returns `false`, `headers` untouched) when tracing is disabled, for
 * non-myosh origins, or when no CSPRNG is available. Never overwrites or
 * removes existing headers.
 *
 * @returns `true` when a `traceparent` header was written.
 */
export const applyTraceparent = (
  headers: MutableHeaders,
  targetUrl: string,
  options: ApplyTraceparentOptions = {},
): boolean => {
  // Off unless the app opted in for this environment — see ./enabled.
  if (!isTracingEnabled()) {
    return false;
  }

  if (!isTraceableOrigin(targetUrl, options.allowedBaseUrl)) {
    return false;
  }

  // Respect an existing upstream trace context — never clobber it.
  if (hasHeader(headers, TRACEPARENT_HEADER)) {
    return false;
  }

  // Validate before writing: the active-context provider is a public seam, so a
  // misconfigured provider must not be able to emit a malformed traceparent.
  const traceparent = getActiveTraceparent();
  if (!traceparent || !isValidTraceparent(traceparent)) {
    return false;
  }

  setHeader(headers, TRACEPARENT_HEADER, traceparent);
  return true;
};
